import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from './AuthShell';
import Toast from '../../components/forms/Toast';
import Icon from '../../components/common/Icon';
import { apiRequest, handleBranding } from '../../utils';

/* Public landing for a networking QR (/meet/:token). Somebody at an event held up their code and
   this visitor scanned it with the plain phone camera.

   ★ The GET only LOOKS; connecting happens on the button tap. Every link previewer and corporate
   mail scanner fetches this URL the moment it is pasted anywhere, so a connection made on mount
   would be made by robots — the same rule the public account page states, and the same failure
   that once put 9,669 "opens" on one brochure.

   ★ Signed out, we send them through the ordinary login, parking this page in the sessionStorage
   `redirect` the login already consumes — so nothing in Login.js has to know this feature exists.
   Deliberately no "type your address and we'll let you in": that is account takeover, and it
   would let anyone holding a forwarded screenshot collect somebody's details under a name they
   invented. */

const MeetConnect = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [person, setPerson] = useState(null);
  const [already, setAlready] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [showToast, setShowToast] = useState(false);

  const fail = useCallback((err) => {
    setError(t(err.message, { defaultValue: t('somethingWentWrong') }));
    setShowToast(true);
  }, [t]);

  useEffect(() => {
    handleBranding();
    (async () => {
      try {
        setInfo(await apiRequest('GET', `/meet/${token}`));
      } catch (err) {
        setInfo({ valid: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const connect = async () => {
    setConnecting(true);
    try {
      const res = await apiRequest('POST', `/meet/${token}/connect`);
      if (res.self) {
        setInfo((prev) => ({ ...prev, is_self: true }));
      } else {
        setPerson(res.person);
        setAlready(!!res.already);
      }
    } catch (err) {
      if (err.message === 'meetSignInFirst') {
        // Come back here once they are in. The login already honours a sessionStorage `redirect`
        // and clears it, so this needs no change there. A ?next= would not survive at all — the
        // SPA strips query parameters on arrival (usePersistUrlParams).
        try {
          window.sessionStorage.setItem('redirect', `/meet/${token}`);
        } catch (_) { /* private mode — they can scan again after logging in */ }
        navigate('/login');
        return;
      }
      fail(err);
    } finally {
      setConnecting(false);
    }
  };

  // `brand` picks the Flaticon brands pack — LinkedIn and Instagram only exist there.
  const link = (url, label, icon, brand = false) => (url ? (
    <a className="auth__hint" href={url} target="_blank" rel="noreferrer" key={label}>
      <Icon name={icon} {...(brand ? { pack: 'brands' } : {})} /> {label}
    </a>
  ) : null);

  const body = () => {
    if (loading) return <div className="auth__card"><span className="auth__spinner" /></div>;
    if (!info || !info.valid) {
      return <div className="auth__card"><p>{t('invalidOrExpiredMeetCode')}</p></div>;
    }
    if (!info.enabled) {
      return <div className="auth__card"><p>{t('meetingNotEnabled')}</p></div>;
    }
    if (info.is_self) {
      return (
        <div className="auth__card">
          <p className="auth__hint"><Icon name="info" /> {t('meetYourself')}</p>
        </div>
      );
    }
    if (person) {
      return (
        <div className="auth__card">
          <p className="auth__hint" style={{ fontWeight: 600 }}>
            <Icon name="user-check" />{' '}
            {already ? t('meetAlreadyConnected') : t('meetConnected')}
          </p>
          <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{person.full_name}</div>
          {(person.role || person.company) && (
            <div className="auth__hint">
              {[person.role, person.company].filter(Boolean).join(' · ')}
            </div>
          )}
          {person.email && link(`mailto:${person.email}`, person.email, 'envelope')}
          {link(person.linkedin, 'LinkedIn', 'linkedin', true)}
          {link(person.instagram, 'Instagram', 'instagram', true)}
          {link(person.website, t('website'), 'globe')}
          <p className="auth__hint">{t('meetMailedBoth')}</p>
          {person.is_public && (
            <button
              type="button"
              className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
              onClick={() => navigate(`/u/${person.id}`)}
            >
              <Icon name="user" /> {t('meetViewProfile')}
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="auth__card">
        <p className="auth__hint" style={{ fontWeight: 600 }}>
          <Icon name="user-add" />{' '}
          {t('meetConnectWith', { name: info.host.first_name || t('someone') })}
        </p>
        <p className="auth__hint">{t('meetExchangeHint')}</p>
        <button
          type="button"
          disabled={connecting}
          className={`auth__primary${localStorage.getItem('primaryColor') ? '' : ' auth__primary--plg'}`}
          onClick={connect}
        >
          <Icon name="user-check" />{' '}
          {info.is_signed_in ? t('meetConnectCta') : t('meetSignInFirst')}
        </button>
      </div>
    );
  };

  return (
    <AuthShell
      title={(info && info.valid && info.event_name) || t('meetTitle')}
      subtitle={info && info.valid ? t('meetSubtitle') : ''}
    >
      {body()}
      <Toast showToast={showToast} setShowToast={setShowToast} message={error} variant="error" />
    </AuthShell>
  );
};

export default MeetConnect;
